import { useEffect, useState, useCallback } from "react";
import { useConfirm } from "@/components/ConfirmProvider";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import Pagination, { PAGE_SIZE } from "@/components/Pagination";
import { Plus, Pencil, Trash2, Award, Gift } from "lucide-react";

const empty = { name: "", mode: "per_visit", visits_required: 5, kg_required: 10, min_kg_per_visit: 0, reward_type: "", reward_value: "", active: true };

export default function Loyalty() {
  const [list, setList] = useState([]);
  const { confirm } = useConfirm();
  const [rewardTypes, setRewardTypes] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [page, setPage] = useState(1);
  // reward catalog management
  const [rtOpen, setRtOpen] = useState(false);
  const [rtName, setRtName] = useState("");
  const [rtEditId, setRtEditId] = useState(null);

  const load = useCallback(() => api.get("/loyalty/rules").then((r) => setList(r.data)), []);
  const loadRewardTypes = useCallback(() => api.get("/loyalty/reward-types").then((r) => setRewardTypes(r.data)), []);
  useEffect(() => { load(); loadRewardTypes(); }, [load, loadRewardTypes]);
  const paged = list.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const activeRewards = rewardTypes.filter((r) => r.active);

  const openNew = () => {
    const first = activeRewards[0]?.name || "";
    setForm({ ...empty, reward_type: first });
    setEditId(null); setOpen(true);
  };

  const save = async () => {
    try {
      if (!form.reward_type) { toast.error("Choisissez un type de récompense"); return; }
      const body = { ...form, visits_required: Number(form.visits_required) || 1, kg_required: Number(form.kg_required) || 0, min_kg_per_visit: Number(form.min_kg_per_visit) || 0 };
      if (editId) await api.put(`/loyalty/rules/${editId}`, body); else await api.post("/loyalty/rules", body);
      toast.success("Règle enregistrée"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => {
    if (!(await confirm({ title: "Supprimer la règle", description: "Supprimer cette règle de fidélité ?", confirmText: "Supprimer", danger: true }))) return;
    try { await api.delete(`/loyalty/rules/${id}`); toast.success("Supprimé"); load(); } catch (e) { toast.error(apiError(e)); }
  };

  // reward type catalog handlers
  const saveRt = async () => {
    const name = rtName.trim();
    if (!name) { toast.error("Nom requis"); return; }
    try {
      if (rtEditId) await api.put(`/loyalty/reward-types/${rtEditId}`, { name, active: true });
      else await api.post("/loyalty/reward-types", { name, active: true });
      setRtName(""); setRtEditId(null); loadRewardTypes();
      toast.success("Récompense enregistrée");
    } catch (e) { toast.error(apiError(e)); }
  };
  const removeRt = async (id) => {
    if (!(await confirm({ title: "Supprimer le type de récompense", description: "Supprimer ce type de récompense ?", confirmText: "Supprimer", danger: true }))) return;
    try { await api.delete(`/loyalty/reward-types/${id}`); loadRewardTypes(); } catch (e) { toast.error(apiError(e)); }
  };

  const selCls = "w-full border border-slate-200 rounded-md px-3 py-2 text-sm bg-white";

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2"><Award className="text-amber-500" /> Fidélité</h1>
        <div className="flex gap-2">
          <Button data-testid="manage-rewards-btn" variant="outline" onClick={() => { setRtName(""); setRtEditId(null); setRtOpen(true); }}><Gift size={18} className="mr-2" /> Gérer les récompenses</Button>
          <Button data-testid="add-loyalty-btn" onClick={openNew} className="bg-sky-600 hover:bg-sky-700"><Plus size={18} className="mr-2" /> Nouvelle règle</Button>
        </div>
      </div>
      <p className="text-slate-500 text-sm">Vous pouvez avoir plusieurs cartes actives en parallèle. Chaque client est rattaché à une carte (choisie sur sa fiche). Un tampon est ajouté automatiquement à chaque commande, selon la carte du client.</p>

      <div className="grid md:grid-cols-2 gap-4">
        {paged.map((r) => (
          <Card key={r.id} data-testid={`loyalty-row-${r.id}`} className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <h3 className="font-semibold text-slate-800">{r.name}</h3>
                <p className="text-sm text-slate-500">
                  {r.mode === "per_kg" ? `Après ${r.kg_required} kg cumulés` : `Après ${r.visits_required} commandes`}
                  {r.mode === "per_visit" && r.min_kg_per_visit > 0 && ` (min. ${r.min_kg_per_visit} kg/commande)`}
                </p>
                <p className="text-sm mt-1">Récompense: <b>{r.reward_type}</b> {r.reward_value && `— ${r.reward_value}`}</p>
              </div>
              <span className={`text-xs px-2 py-1 rounded ${r.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{r.active ? "Active" : "Inactive"}</span>
            </div>
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => { setForm({ name: r.name, mode: r.mode || "per_visit", visits_required: r.visits_required ?? 5, kg_required: r.kg_required ?? 10, min_kg_per_visit: r.min_kg_per_visit ?? 0, reward_type: r.reward_type || "", reward_value: r.reward_value || "", active: r.active }); setEditId(r.id); setOpen(true); }}><Pencil size={14} className="mr-1" /> Modifier</Button>
              <Button size="sm" variant="outline" className="text-red-600" onClick={() => remove(r.id)}><Trash2 size={14} className="mr-1" /> Supprimer</Button>
            </div>
          </Card>
        ))}
      </div>

      <Pagination page={page} total={list.length} onPage={setPage} />

      {/* Rule dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editId ? "Modifier la règle" : "Nouvelle règle"}</DialogTitle>
            <DialogDescription>Définissez le mode de comptage, le seuil et la récompense.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Nom</Label><Input data-testid="loyalty-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Mode de comptage</Label>
              <select data-testid="loyalty-mode" className={selCls} value={form.mode} onChange={(e) => setForm({ ...form, mode: e.target.value })}>
                <option value="per_visit">Par passage (1 tampon / commande)</option>
                <option value="per_kg">Par kilo (cumul des kg)</option>
              </select>
            </div>
            {form.mode === "per_kg" ? (
              <div><Label>Nombre de kg requis</Label><Input data-testid="loyalty-kg" type="number" value={form.kg_required} onChange={(e) => setForm({ ...form, kg_required: e.target.value })} /><p className="text-xs text-slate-400 mt-1">Seuls les services facturés au kg sont comptés.</p></div>
            ) : (
              <>
                <div><Label>Nombre de passages requis</Label><Input data-testid="loyalty-visits" type="number" value={form.visits_required} onChange={(e) => setForm({ ...form, visits_required: e.target.value })} /></div>
                <div><Label>Kg minimum par passage (0 = aucun)</Label><Input data-testid="loyalty-min-kg" type="number" value={form.min_kg_per_visit} onChange={(e) => setForm({ ...form, min_kg_per_visit: e.target.value })} /><p className="text-xs text-slate-400 mt-1">Une commande en dessous de ce poids ne donne pas de tampon.</p></div>
              </>
            )}
            <div><Label>Type de récompense</Label>
              <select data-testid="loyalty-reward-type" className={selCls} value={form.reward_type} onChange={(e) => setForm({ ...form, reward_type: e.target.value })}>
                <option value="">— Choisir —</option>
                {activeRewards.map((rt) => <option key={rt.id} value={rt.name}>{rt.name}</option>)}
              </select>
              <p className="text-xs text-slate-400 mt-1">Gérez la liste via « Gérer les récompenses ».</p>
            </div>
            <div><Label>Détail de la récompense</Label><Input data-testid="loyalty-reward-value" value={form.reward_value} onChange={(e) => setForm({ ...form, reward_value: e.target.value })} placeholder="ex: -20%, 1 kg offert..." /></div>
            <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
            <Button data-testid="save-loyalty-btn" onClick={save} className="bg-sky-600 hover:bg-sky-700">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reward types catalog dialog */}
      <Dialog open={rtOpen} onOpenChange={setRtOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Types de récompenses</DialogTitle>
            <DialogDescription>Ajoutez, modifiez ou supprimez les récompenses proposées dans vos règles.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {rewardTypes.map((rt) => (
              <div key={rt.id} data-testid={`reward-type-${rt.id}`} className="flex items-center justify-between rounded-lg border border-slate-100 p-2">
                <span className="text-sm text-slate-700">{rt.name}</span>
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => { setRtName(rt.name); setRtEditId(rt.id); }}><Pencil size={15} /></Button>
                  <Button size="icon" variant="ghost" className="text-red-600" onClick={() => removeRt(rt.id)}><Trash2 size={15} /></Button>
                </div>
              </div>
            ))}
            {rewardTypes.length === 0 && <p className="text-sm text-slate-400">Aucune récompense. Ajoutez-en une.</p>}
          </div>
          <div className="flex gap-2 items-end pt-2 border-t border-slate-100">
            <div className="flex-1"><Label>{rtEditId ? "Modifier la récompense" : "Nouvelle récompense"}</Label><Input data-testid="reward-type-name" value={rtName} onChange={(e) => setRtName(e.target.value)} placeholder="ex: Lavage offert" /></div>
            <Button data-testid="save-reward-type-btn" onClick={saveRt} className="bg-sky-600 hover:bg-sky-700">{rtEditId ? "Modifier" : "Ajouter"}</Button>
            {rtEditId && <Button variant="outline" onClick={() => { setRtName(""); setRtEditId(null); }}>Annuler</Button>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
